
import { defineComponent, onMounted, ref } from 'vue'
import ContentLayout from '@/views/layout/ContentLayout.vue'
import { User } from '@/repositories/data'
import { Company } from '@/repositories/system/data'
import { repositories } from '@/repositories/system/repositories'
import { callApiWrapper } from '@/views/callApiWrapper'
import { useRoute, useRouter } from 'vue-router'
import { object } from 'yup'
import { myValidationSchemaObj } from '@/views/validation'
import { useForm } from 'vee-validate'
import AddMailAddressDialog from '@/views/system/AddMailAddressDialog.vue'
import { services } from '@/services/services'
import { RequestParams } from '@/services/deliverySlipService'
import ConfirmSendSlipDialog from '@/views/system/ConfirmSendSlipDialog.vue'
import DismissibleAlert from '@/views/components/DismissibleAlert.vue'

const companyRepository = repositories.company
const userRepository = repositories.user
const service = services.deliverySlip

interface InputForm {
  deliveryDate: string;
  userKey: string;
  subject: string;
  body: string;
}

const validationSchema = object({
  deliveryDate: myValidationSchemaObj.requiredDate,
  userKey: myValidationSchemaObj.requiredString,
  subject: myValidationSchemaObj.requiredString,
  body: myValidationSchemaObj.requiredString
})

export default defineComponent({
  components: {
    DismissibleAlert,
    ConfirmSendSlipDialog,
    AddMailAddressDialog,
    ContentLayout
  },
  setup () {
    const route = useRoute()
    const router = useRouter()
    const loading = ref(false)
    const requestError = ref<string | null>(null)
    const showingAddDialog = ref(false)
    const showingConfirmDialog = ref(false)
    const isSavedDraft = ref(false)
    const isModified = ref(false)
    const isReadOnly = ref(false)
    const company = ref<Company>({
      key: 'dummy',
      name: '',
      isAvailable: true
    })
    const adminUsers = ref<User[]>([])
    const ccList = ref<string[]>([])

    const { errors, values, meta, validate } = useForm<InputForm>({
      validationSchema: validationSchema,
      validateOnMount: false
    })

    onMounted(async () => {
      loading.value = true
      const companyKey = route.params.companyKey as string
      await callApiWrapper(requestError, async () => {
        company.value = await companyRepository.get(companyKey)
        adminUsers.value = await userRepository.adminList(companyKey)

        if (route.params.serialNo) {
          const serialNo = Number.parseInt(route.params.serialNo as string)
          await getDetail(companyKey, serialNo)
          isSavedDraft.value = true
        } else {
          const defaultDraft = await service.getDefaultDraft()
          values.subject = defaultDraft.subject
          values.body = defaultDraft.body
        }
      })
      await validate()
      loading.value = false
    })

    const saveDraft = async () => {
      loading.value = true
      const companyKey = route.params.companyKey as string
      const params = createRequestParams()

      await callApiWrapper(requestError, async () => {
        if (route.params.serialNo) {
          const serialNo = Number.parseInt(route.params.serialNo as string)
          await service.updateDraft(companyKey, serialNo, params)
          await getDetail(companyKey, serialNo)
        } else {
          const response = await service.addDraft(companyKey, params)
          await router.push({
            name: 'EditDraftDeliverySlip',
            params: {
              companyKey: companyKey,
              serialNo: response.serialNo
            }
          })
        }
      })

      isSavedDraft.value = true
      isModified.value = false
      loading.value = false
    }

    const addMailAddress = (mailAddress: string) => {
      ccList.value.push(mailAddress)
      isModified.value = true
    }

    const removeMailAddress = (mailAddress: string) => {
      ccList.value = ccList.value.filter(cc => cc !== mailAddress)
      isModified.value = true
    }

    const sendSlip = async () => {
      await router.push({
        name: 'DeliverySlipList',
        params: {
          companyKey: company.value.key
        }
      })
    }

    const onModified = async () => {
      await validate()
      isModified.value = true
    }

    function createRequestParams (): RequestParams {
      return {
        deliveryDate: values.deliveryDate,
        userKey: values.userKey,
        ccList: ccList.value,
        subject: values.subject,
        body: values.body
      }
    }

    async function getDetail (companyKey: string, serialNo: number) {
      const response = await service.getDetail(companyKey, serialNo)
      values.deliveryDate = response.deliveryDate
      values.userKey = response.userKey
      values.subject = response.subject
      values.body = response.body
      ccList.value = response.ccList
      isReadOnly.value = !response.isDraft
    }

    return {
      loading,
      requestError,
      showingAddDialog,
      showingConfirmDialog,
      company,
      adminUsers,
      errors,
      values,
      ccList,
      isSavedDraft,
      isModified,
      isReadOnly,
      meta,
      onModified,
      saveDraft,
      addMailAddress,
      removeMailAddress,
      sendSlip
    }
  }
})
